/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { concat, of, from, defer } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import {
    mergeMap,
    filter,
    catchError,
    toArray,
    mergeAll,
    map,
    withLatestFrom,
    pluck,
} from 'rxjs/operators';
import { ofType } from 'redux-observable';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import { SecurityHelper, STATUS } from 'sarsaparilla';

import { getTourAvailabilityUrl } from '../api/endpoints';

import {
    fetchTourAvailabilitySuccess,
    fetchTourAvailabilityError,
    setFetchTourAvailabilityStatus,
    FETCH_TOUR_AVAILABILITY,
    ADDITIONAL_MONTHS_TO_FETCH,
} from '../actions/tourAvailabilityActions';

import { setDateFilter } from '../actions/filtersActions';
import { includeFirstComeFirstServed as includeFirstComeFirstServedUtil } from '../utils/generalTicketUtils';
import { MAX_STALE_TIME } from '../constants';

/**
 * Helper function to format the API response
 */
function transformResponses({
    responses,
    tourId,
    isAvailableState,
    isNotReleasedState,
    lastFetched,
    accountType,
    isSoldOutState,
    isFirstComeFirstServedState,
    nextReleaseState,
    includeFirstComeFirstServed,
    isScheduledState,
}) {
    const isAvailableByDate = { ...isAvailableState };
    const isNotReleasedByDate = { ...isNotReleasedState };
    const isSoldOutByDate = { ...isSoldOutState };
    const isFirstComeFirstServedByDate = { ...isFirstComeFirstServedState };
    const nextReleaseByDate = { ...nextReleaseState };
    const isScheduledByDate = { ...isScheduledState };
    const now = moment();

    if (!Array.isArray(responses)) {
        return null;
    }

    responses.forEach((data) => {
        if (isEmpty(data)) {
            return;
        }

        const dayKeys = Object.keys(data);

        dayKeys.forEach((day) => {
            const dayData = data[day];
            const dataByTourId = dayData?.tour_availability_summary_view_by_tour_id || {};
            const monthKey = day.slice(0, -3);

            const hasReservable = dataByTourId?.[tourId]?.has_reservable;
            const hasNotYetReleased = dataByTourId?.[tourId]?.has_not_yet_released;
            const nextReleaseTime = dataByTourId?.[tourId]?.next_release_timestamp;
            const reservedCount = dataByTourId?.[tourId]?.reserved_count;
            const scheduledCount = dataByTourId?.[tourId]?.scheduled_count;
            const hasWalkUp = dataByTourId?.[tourId]?.has_walk_up;

            // Update availability counts
            if (isNil(isAvailableByDate[monthKey])) {
                isAvailableByDate[monthKey] = {};
            }
            isAvailableByDate[monthKey][day] = hasReservable;

            // update scheduled counts
            if (isNil(isScheduledByDate[monthKey])) {
                isScheduledByDate[monthKey] = {};
            }
            isScheduledByDate[monthKey][day] = scheduledCount;

            // Update unreleased counts
            if (isNil(isNotReleasedByDate[monthKey])) {
                isNotReleasedByDate[monthKey] = {};
            }
            isNotReleasedByDate[monthKey][day] = hasNotYetReleased;

            // Update sold out dates
            if (isNil(isSoldOutByDate[monthKey])) {
                isSoldOutByDate[monthKey] = {};
            }

            isSoldOutByDate[monthKey][day] =
                !!reservedCount && !!scheduledCount && reservedCount >= scheduledCount;

            // Update first come first served dates
            if (isNil(isFirstComeFirstServedByDate[monthKey])) {
                isFirstComeFirstServedByDate[monthKey] = {};
            }

            if (includeFirstComeFirstServed) {
                isFirstComeFirstServedByDate[monthKey][day] = hasWalkUp;
            }

            // Update next release time
            if (isNil(nextReleaseByDate[monthKey])) {
                nextReleaseByDate[monthKey] = {};
            }

            nextReleaseByDate[monthKey][day] = nextReleaseTime;

            if (
                nextReleaseTime &&
                now.isAfter(moment(nextReleaseTime)) &&
                hasNotYetReleased
            ) {
                isAvailableByDate[monthKey][day] = hasNotYetReleased;
            }
        });
    });

    return {
        tourId,
        isAvailableByDate,
        isNotReleasedByDate,
        isSoldOutByDate,
        isFirstComeFirstServedByDate,
        lastFetched,
        lastFetchedAccountType: accountType,
        nextReleaseByDate,
        isScheduledByDate,
    };
}

/**
 * Fetches availability for individual tour in a facility for x number of months
 * - where x = ADDITIONAL_MONTHS_TO_FETCH
 *
 * We store the fetch time as well, and when the user navigates the calendar,
 * we only pull fresh data if the existing data is stale (over 1 min old)
 */
export const fetchAvailabilityByTour = (actions$, state$) =>
    actions$.pipe(
        ofType(FETCH_TOUR_AVAILABILITY),
        filter(
            ({ payload }) => !!payload?.facilityId && !!payload?.tourId && !!payload?.date
        ),
        withLatestFrom(state$.pipe(pluck('timedEntry'))),
        mergeMap(([{ payload }, timedEntryState]) => {
            const availability = timedEntryState.tourAvailability;
            const accountType = timedEntryState.accountType.accountType;

            const { facilityId, tourId, date, forceRefresh } = payload;

            const includeFirstComeFirstServed =
                includeFirstComeFirstServedUtil(facilityId);
            const startDate = date.clone().startOf('month');
            const lastFetchedState = availability?.lastFetched || {};
            const lastFetchedAccountType = availability?.lastFetchedAccountType;
            const isAccountTypeChanged =
                Boolean(lastFetchedAccountType) && accountType !== lastFetchedAccountType;
            const isAvailableState = availability?.isAvailableByDate || {};
            const isNotReleasedState = availability?.isNotReleasedByDate || {};
            const isSoldOutState = availability?.isSoldOutByDate || {};
            const isFirstComeFirstServedState =
                availability?.isFirstComeFirstServedByDate || {};
            const nextReleaseState = availability?.nextReleaseByDate || {};
            const isScheduledState = availability?.isScheduledByDate || {};

            const lastFetched = { ...lastFetchedState };
            const now = new Date().getTime();

            /**
             * Delete stale data from our cached states
             */
            const lastFetchedKeys = Object.keys(lastFetchedState);
            lastFetchedKeys.forEach((monthKey) => {
                const lastFetchedTime = lastFetchedState[monthKey];
                const isStale = now - lastFetchedTime > MAX_STALE_TIME;

                if (forceRefresh || isStale || isAccountTypeChanged) {
                    delete isAvailableState[monthKey];
                    delete isNotReleasedState[monthKey];
                    delete isSoldOutState[monthKey];
                    delete isFirstComeFirstServedState[monthKey];
                }
                if (forceRefresh) {
                    delete lastFetchedState[monthKey];
                    delete isAvailableState[monthKey];
                    delete isNotReleasedState[monthKey];
                    delete isSoldOutState[monthKey];
                    delete isFirstComeFirstServedState[monthKey];
                }
            });

            /**
             * TODO: prez - refactor this to remove ADDITIONAL_MONTHS_TO_FETCH
             * Create array of dates to fetch. We need to fetch the requested month
             */
            const monthsToFetch = [];

            // Forward months
            for (let i = 0; i < ADDITIONAL_MONTHS_TO_FETCH; i += 1) {
                const month = startDate.clone().add(i, 'months');
                const monthKey = month.format('YYYY-MM');

                if (
                    isAccountTypeChanged ||
                    !lastFetchedState[monthKey] ||
                    now - lastFetchedState[monthKey] > MAX_STALE_TIME
                ) {
                    monthsToFetch.push(month);
                }
            }

            // Update lastFetched
            monthsToFetch.forEach((month) => {
                const formattedMonth = month.format('YYYY-MM');
                lastFetched[formattedMonth] = now;
            });

            // Create array of deferred requests
            const requests = monthsToFetch.map((item) => {
                const month = item.format('MM');
                const year = item.format('YYYY');
                const url = getTourAvailabilityUrl({
                    facilityId,
                    month,
                    year,
                    accountType,
                    tourId,
                });

                return defer(() =>
                    ajax({
                        url,
                        method: 'GET',
                        headers: {
                            Authorization: SecurityHelper.getAuthHeader(),
                        },
                    })
                        .pipe(pluck('response'))
                        .pipe(
                            pluck('facility_availability_summary_view_by_local_date'),
                            catchError((error) => {
                                // if this error, let it process as empty dates for the month rather
                                // than throwing an error to the user
                                if (error?.response?.error === 'invalid year/month') {
                                    return of({});
                                }
                                throw error;
                            })
                        )
                );
            });

            // ajax call stream, calls 5 at a time
            const ajax$ = from(requests).pipe(mergeAll(5), toArray());

            const maybeResetDate = isAccountTypeChanged
                ? setDateFilter(null)
                : { type: 'null' };

            return concat(
                of(setFetchTourAvailabilityStatus(STATUS.PENDING)),
                of(maybeResetDate),
                ajax$.pipe(
                    map((responses) => {
                        const data = transformResponses({
                            responses,
                            tourId,
                            isAvailableState,
                            isNotReleasedState,
                            lastFetched,
                            accountType,
                            isSoldOutState,
                            isFirstComeFirstServedState,
                            nextReleaseState,
                            includeFirstComeFirstServed,
                            isScheduledState,
                        });
                        return fetchTourAvailabilitySuccess(data);
                    }),
                    catchError((error) => {
                        return of(fetchTourAvailabilityError(error));
                    })
                )
            );
        })
    );
